import {
  Money,
  TaxRate,
  TaxStrategy,
  getTaxRateByRegion,
  roundTo2Decimals,
} from '@principle-theorem/accounting';
import {
  DestinationEntityRecordStatus,
  IDestinationEntityJobRunOptions,
  IMigratedDataSummary,
  ITranslationMap,
  SkippedDestinationEntityRecord,
  type FailedDestinationEntityRecord,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IGetRecordResponse,
  type IPracticeMigration,
  type ISourceEntityRecord,
  type MergeConflictDestinationEntityRecord,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  BuildAccountAggregates,
  Invoice,
  Patient,
  Transaction,
  TransactionOperators,
  productToLineItem,
} from '@principle-theorem/principle-core';
import {
  InvoiceStatus,
  TransactionProvider,
  TransactionStatus,
  TransactionType,
  type IAppointment,
  type IFeeLineItem,
  type IInvoice,
  type IPatient,
  type IPractice,
  type IProductLineItem,
  type ITransaction,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  FirestoreMigrate,
  PrimaryKey,
  WithPrimaryKey,
  getError,
  resolveSequentially,
  safeCombineLatest,
  snapshotCombineLatest,
  sortByCreatedAt,
  toMomentTz,
  toTimestamp,
  type DocumentReference,
  type IIdentifiable,
  type WithRef,
} from '@principle-theorem/shared';
import { compact } from 'lodash';
import { combineLatest, of, type Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { BaseDestinationEntity } from '../../../destination/base-destination-entity';
import { DestinationEntity } from '../../../destination/destination-entity';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import { PATIENT_APPOINTMENT_RESOURCE_TYPE } from '../../../destination/entities/patient-appointments';
import { PATIENT_INVOICE_RESOURCE_TYPE } from '../../../destination/entities/patient-invoices';
import { ItemCodeResourceMapType } from '../../../mappings/item-codes-to-xlsx';
import { SourceEntityRecord } from '../../../source/source-entity-record';
import { type TranslationMapHandler } from '../../../translation-map';
import {
  PatientAppointmentInvoiceAdjustmentSourceEntity,
  PraktikaAppointmentInvoiceAdjustment,
} from '../../source/entities/appointment-invoice-adjustment';
import {
  PatientAppointmentInvoicePaymentSourceEntity,
  PraktikaAppointmentInvoicePayment,
  PraktikaTransactionEffect,
  PraktikaTransationType,
} from '../../source/entities/appointment-invoice-payment';
import {
  PatientAppointmentProcedureSourceEntity,
  type IPraktikaAppointmentProcedure,
} from '../../source/entities/appointment-procedure';
import { PatientSourceEntity } from '../../source/entities/patient';
import {
  PatientAppointmentSourceEntity,
  PraktikaAppointment,
  type IPraktikaAppointment,
} from '../../source/entities/patient-appointment';
import {
  PraktikaItemCodeMappingHandler,
  resolveMappedCode,
} from '../mappings/item-code';
import { PraktikaPracticeMappingHandler } from '../mappings/practices';
import { PraktikaStafferMappingHandler } from '../mappings/staff';
import { PatientAppointmentDestinationEntity } from './patient-appointments';
import { PatientDepositDestinationEntity } from './patient-deposits';
import { PatientTreatmentPlanDestinationEntity } from './patient-treatment-plans';
import { PatientDestinationEntity } from './patients';
import { StafferDestinationEntity } from './staff';
import { buildInvoiceForAppointment } from './lib/praktika-invoice-builder';
import { PraktikaSurcharge } from './lib/praktika-surcharge';

interface IInvoiceBuildData {
  invoice: IInvoice;
  // accountCreditAmount: number;
  transactions: (IIdentifiable & ITransaction)[];
}

interface IInvoiceUpdateData {
  invoiceRef: DocumentReference<IInvoice>;
  transactionRefs: DocumentReference<ITransaction>[];
}

interface IInvoiceSuccessData extends IInvoiceUpdateData {
  sourceRef: PrimaryKey<ISourceEntityRecord>;
  invoiceRef: DocumentReference<IInvoice>;
  transactionRefs: DocumentReference<ITransaction>[];
}

export const PATIENT_INVOICE_DESTINATION_ENTITY = DestinationEntity.init({
  metadata: {
    key: PATIENT_INVOICE_RESOURCE_TYPE,
    label: 'Patient Invoices',
    description: `Each service code will be nested under a "Custom Service Code" treatment on the invoice. Products will be shown as normal.

    If a refund to card needs to be done for a Praktika payment, it will need to be performed in Praktika, and record a manual refund in Principle.
    Transactions, invoice adjustments, refunds etc... will be done as expected in Principle.`,
  },
});

export const PATIENT_INVOICE_CUSTOM_MAPPING_TYPE = 'patientInvoice';

export interface IPatientInvoiceJobData {
  sourceAppointment: IGetRecordResponse<PraktikaAppointment>;
  sourceItemCodes: WithPrimaryKey<
    ITranslationMap<object, ItemCodeResourceMapType>
  >[];
}

export class PatientInvoiceDestinationEntity extends BaseDestinationEntity<
  IInvoiceSuccessData,
  IPatientInvoiceJobData,
  IPatientInvoiceJobData
> {
  destinationEntity = PATIENT_INVOICE_DESTINATION_ENTITY;
  override canMigrateByDateRange = true;

  sourceCountComparison = new PatientAppointmentSourceEntity();

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    appointments: new PatientAppointmentSourceEntity(),
    appointmentProcedures: new PatientAppointmentProcedureSourceEntity(),
    invoicePayments: new PatientAppointmentInvoicePaymentSourceEntity(),
    invoiceAdjustments: new PatientAppointmentInvoiceAdjustmentSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
    treatmentPlans: new PatientTreatmentPlanDestinationEntity(),
    appointments: new PatientAppointmentDestinationEntity(),
    deposits: new PatientDepositDestinationEntity(),
    staff: new StafferDestinationEntity(),
  };

  customMappings = {
    staff: new PraktikaStafferMappingHandler(),
    practice: new PraktikaPracticeMappingHandler(),
    itemCodes: new PraktikaItemCodeMappingHandler(),
  };

  sourceCountDataAccessor(
    data: IPatientInvoiceJobData
  ): PrimaryKey<ISourceEntityRecord> {
    return data.sourceAppointment.record.id;
  }

  getMigratedData$(
    record: IDestinationEntityRecord<IInvoiceSuccessData>
  ): Observable<IMigratedDataSummary[]> {
    if (record.status !== DestinationEntityRecordStatus.Migrated) {
      return of([]);
    }

    return combineLatest([
      Firestore.getDoc(record.data.invoiceRef),
      safeCombineLatest(
        record.data.transactionRefs.map((transactionRef) =>
          Firestore.getDoc(transactionRef)
        )
      ),
    ]).pipe(
      map(([invoiceRef, transactions]) => {
        const data: IMigratedDataSummary[] = [
          {
            label: 'Invoice',
            data: invoiceRef,
          },
        ];

        data.push(
          ...transactions.map((transaction) => ({
            label: 'Transactions',
            data: transaction,
          }))
        );

        return data;
      })
    );
  }

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IPatientInvoiceJobData[]> {
    const sourceItemCodes$ =
      this.customMappings.itemCodes.getRecords(translationMap);

    return snapshotCombineLatest([sourceItemCodes$]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sourceEntities.appointments,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(([sourceAppointments, [sourceItemCodes]]) =>
        sourceAppointments.map((sourceAppointment) => ({
          sourceAppointment,
          sourceItemCodes,
        }))
      )
    );
  }

  getDestinationEntityRecordUid(data: IPatientInvoiceJobData): string {
    return data.sourceAppointment.record.uid;
  }

  buildMigrationData(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    data: IPatientInvoiceJobData
  ):
    | IPatientInvoiceJobData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord) {
    return data;
  }

  hasMergeConflict(
    _translationMap: TranslationMapHandler,
    _data: IPatientInvoiceJobData
  ): IPatientInvoiceJobData | undefined {
    return;
  }

  buildMergeConflictRecord(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    jobData: IPatientInvoiceJobData,
    migrationData: IPatientInvoiceJobData
  ): IDestinationEntityRecord & MergeConflictDestinationEntityRecord {
    return {
      uid: migrationData.sourceAppointment.record.uid,
      label: migrationData.sourceAppointment.record.label,
      status: DestinationEntityRecordStatus.MergeConflict,
      sourceRef: jobData.sourceAppointment.record.id,
    };
  }

  async runJob(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMapHandler: TranslationMapHandler,
    data: IPatientInvoiceJobData
  ): Promise<IDestinationEntityRecord> {
    const appointmentId = data.sourceAppointment.data.appointment_id.toString();
    const patientId =
      data.sourceAppointment.data.appointment_patientid.toString();
    const patientMap = await translationMapHandler.getDestination<IPatient>(
      patientId,
      PATIENT_RESOURCE_TYPE
    );
    const appointmentMap =
      await translationMapHandler.getDestination<IAppointment>(
        appointmentId,
        PATIENT_APPOINTMENT_RESOURCE_TYPE
      );

    if (!patientMap || !appointmentMap) {
      const message: string[] = [];
      if (!patientMap) {
        message.push(`No patient for id ${patientId}`);
      }
      if (!appointmentMap) {
        message.push(`No appointment for id ${appointmentId}`);
      }

      return this._buildErrorResponse(
        data.sourceAppointment,
        message.join('; ')
      );
    }

    const procedures =
      await this.sourceEntities.appointmentProcedures.filterRecords(
        migration,
        'appointmentId',
        appointmentId
      );

    const backupDate = toMomentTz(
      migration.configuration.backupDate,
      migration.configuration.timezone
    );
    if (
      !data.sourceAppointment.data.appointment_taxinvoice_id ||
      !procedures.length ||
      (data.sourceAppointment.record.translations.from &&
        toMomentTz(
          data.sourceAppointment.record.translations.from,
          migration.configuration.timezone
        ).isAfter(backupDate.endOf('day')))
    ) {
      return this._buildSuccessResponse(data.sourceAppointment);
    }

    const invoicePayments =
      await this.sourceEntities.invoicePayments.filterRecords(
        migration,
        'appointmentId',
        appointmentId
      );

    const invoiceAdjustments =
      await this.sourceEntities.invoiceAdjustments.filterRecords(
        migration,
        'appointmentId',
        appointmentId
      );

    const productProcedures = procedures
      .filter((procedure) => {
        const isScheduled = SourceEntityRecord.filterAccessor(
          procedure.record,
          'isScheduled'
        );
        const mappedCode = resolveMappedCode(
          data.sourceItemCodes,
          procedure.data
        );

        const isMappedCode =
          mappedCode && mappedCode !== ItemCodeResourceMapType.Omit;

        return isScheduled && !isMappedCode;
      })
      .map((procedure) => procedure.data);

    const billedProcedures = procedures
      .filter((procedure) => {
        const isScheduled = SourceEntityRecord.filterAccessor(
          procedure.record,
          'isScheduled'
        );
        const isOmitted =
          resolveMappedCode(data.sourceItemCodes, procedure.data) ===
          ItemCodeResourceMapType.Omit;

        return isScheduled && !isOmitted;
      })
      .map((procedure) => procedure.data);

    try {
      const invoiceData = await this._buildInvoiceData(
        data.sourceAppointment,
        appointmentMap,
        patientMap,
        invoicePayments,
        invoiceAdjustments,
        productProcedures,
        getSurchargeTaxStatus(billedProcedures),
        migration
      );

      const updateRefs = await this._upsertInvoice(
        invoiceData,
        appointmentId,
        translationMapHandler,
        appointmentMap,
        patientMap
      );

      const accountAggregateBuilder = new BuildAccountAggregates(console);
      await accountAggregateBuilder.updatePatient(patientMap, false);

      return this._buildSuccessResponse(data.sourceAppointment, updateRefs);
    } catch (error) {
      return this._buildErrorResponse(data.sourceAppointment, getError(error));
    }
  }

  buildSkippedResponse(
    data: IPatientInvoiceJobData
  ): IDestinationEntityRecord & SkippedDestinationEntityRecord {
    return {
      uid: data.sourceAppointment.record.uid,
      label: data.sourceAppointment.record.label,
      status: DestinationEntityRecordStatus.Skipped,
      sourceRef: data.sourceAppointment.record.id,
    };
  }

  private _buildSuccessResponse(
    appointment: IGetRecordResponse<PraktikaAppointment>,
    updateData?: IInvoiceUpdateData
  ): IDestinationEntityRecord {
    return {
      uid: appointment.record.uid,
      label: appointment.record.label,
      data: {
        sourceRef: appointment.record.id,
        ...updateData,
      },
      status: DestinationEntityRecordStatus.Migrated,
      sourceRef: appointment.record.id,
      migratedAt: toTimestamp(),
    };
  }

  private async _upsertInvoice(
    invoiceData: IInvoiceBuildData,
    appointmentUid: string,
    translationMap: TranslationMapHandler,
    appointmentRef: DocumentReference<IAppointment>,
    patientRef: DocumentReference<IPatient>
  ): Promise<IInvoiceUpdateData> {
    const invoiceDestinationRef = await translationMap.getDestination(
      appointmentUid,
      PATIENT_INVOICE_CUSTOM_MAPPING_TYPE
    );

    const invoiceRef = await FirestoreMigrate.upsertDoc(
      Patient.invoiceCol({
        ref: patientRef,
      }),
      { ...invoiceData.invoice },
      invoiceDestinationRef?.id
    );

    if (!invoiceDestinationRef) {
      await translationMap.upsert({
        sourceIdentifier: appointmentUid,
        destinationIdentifier: invoiceRef,
        resourceType: PATIENT_INVOICE_CUSTOM_MAPPING_TYPE,
      });
    }

    await FirestoreMigrate.patchDoc(appointmentRef, {
      invoiceRef,
    });

    const transactionRefs = await resolveSequentially(
      invoiceData.transactions,
      (transaction) =>
        FirestoreMigrate.upsertDoc(
          Invoice.transactionCol({
            ref: invoiceRef,
          }),
          transaction,
          transaction.uid
        )
    );

    return {
      invoiceRef,
      transactionRefs,
    };
  }

  private async _buildInvoiceData(
    sourceAppointment: IGetRecordResponse<PraktikaAppointment>,
    appointmentRef: DocumentReference<IAppointment>,
    patientRef: DocumentReference<IPatient>,
    invoicePayments: IGetRecordResponse<PraktikaAppointmentInvoicePayment>[],
    invoiceAdjustments: IGetRecordResponse<PraktikaAppointmentInvoiceAdjustment>[],
    productProcedures: IPraktikaAppointmentProcedure[],
    surchargeTaxStatus: TaxStrategy,
    migration: WithRef<IPracticeMigration>
  ): Promise<IInvoiceBuildData> {
    const appointment = await Firestore.getDoc(appointmentRef);
    if (!sourceAppointment.data.appointment_taxinvoice_id) {
      throw Error('No invoice for appointment');
    }

    const organisation = await Firestore.getDoc(
      migration.configuration.organisation.ref
    );
    const taxRate = getTaxRateByRegion(organisation.region);

    const invoice = await buildInvoiceForAppointment(
      appointment,
      patientRef,
      taxRate,
      sourceAppointment.record.translations.createdAt
    );
    invoice.reference =
      sourceAppointment.data.appointment_taxinvoice_id.toString();

    invoice.items.push(...getProducts(productProcedures, taxRate));
    invoice.items.push(
      ...getSurcharges(invoiceAdjustments, taxRate, surchargeTaxStatus)
    );

    const transactions = [
      ...getTransactions(invoicePayments, invoice.practice.ref),
      ...getAdjustments(invoiceAdjustments, invoice.practice.ref),
      ...getUsedAccountCredits(invoiceAdjustments, invoice.practice.ref),
    ];

    const status = getInvoiceStatus(
      sourceAppointment.data,
      Invoice.balance(invoice, transactions)
    );

    const lastTransaction = new TransactionOperators(transactions)
      .completed()
      .sort(sortByCreatedAt)
      .reverse()
      .last();

    if (status === InvoiceStatus.Paid && lastTransaction) {
      invoice.paidAt = lastTransaction.createdAt;
    }

    const issuedAt =
      sourceAppointment.record.translations.from ??
      sourceAppointment.record.translations.createdAt;

    if (status !== InvoiceStatus.Draft) {
      invoice.issuedAt = issuedAt;
    }

    Invoice.updateStatus(invoice, status, issuedAt);

    return {
      invoice,
      transactions,
    };
  }

  private _buildErrorResponse(
    appointment: IGetRecordResponse<PraktikaAppointment>,
    errorMessage?: string
  ): IDestinationEntityRecord {
    return {
      uid: appointment.record.uid,
      label: appointment.record.label,
      status: DestinationEntityRecordStatus.Failed,
      sourceRef: appointment.record.id,
      errorMessage: errorMessage ?? 'Missing required properties for invoice',
      failData: {
        appointmentRef: appointment.record.id,
      },
    };
  }
}

function getProducts(
  procedures: IPraktikaAppointmentProcedure[],
  taxRate: TaxRate
): IProductLineItem[] {
  return compact(
    procedures.map((procedure) => {
      if (procedure.ada_code) {
        return;
      }

      const taxStatus = procedure.has_gst
        ? TaxStrategy.GSTApplicable
        : TaxStrategy.GSTFree;
      const cost = roundTo2Decimals(parseFloat(procedure.total_fee));

      return productToLineItem(
        {
          name: procedure.description,
          cost,
          taxStatus,
        },
        taxRate
      );
    })
  );
}

function getSurchargeTaxStatus(
  procedures: IPraktikaAppointmentProcedure[]
): TaxStrategy {
  return procedures.some((procedure) => procedure.has_gst)
    ? TaxStrategy.GSTApplicable
    : TaxStrategy.GSTFree;
}

function getSurcharges(
  adjustments: IGetRecordResponse<PraktikaAppointmentInvoiceAdjustment>[],
  taxRate: TaxRate,
  taxStatus: TaxStrategy
): IFeeLineItem[] {
  return adjustments
    .filter((adjustmentRecord) =>
      PraktikaSurcharge.isSurcharge(adjustmentRecord.data)
    )
    .map((adjustmentRecord) =>
      PraktikaSurcharge.toFeeLineItem(adjustmentRecord.data, taxRate, taxStatus)
    );
}

function getInvoiceStatus(
  appointment: IPraktikaAppointment,
  amountRemaining: number
): InvoiceStatus {
  if (amountRemaining === 0) {
    return InvoiceStatus.Paid;
  }

  if (!appointment.appointment_taxinvoice_date) {
    return InvoiceStatus.Draft;
  }

  if (amountRemaining > 0) {
    return InvoiceStatus.Issued;
  }

  if (amountRemaining < 0) {
    // eslint-disable-next-line no-console
    console.error(`Invoice overpaid by $${amountRemaining}`);
    return InvoiceStatus.Paid;
  }

  return InvoiceStatus.Draft;
}

function getTransactions(
  payments: IGetRecordResponse<PraktikaAppointmentInvoicePayment>[],
  practiceRef: DocumentReference<IPractice>
): (IIdentifiable & ITransaction)[] {
  return payments.map((paymentRecord) => {
    const payment = paymentRecord.data;
    const uid = payment.id.toString();
    return {
      ...Transaction.init({
        ...Transaction.internalReference(TransactionProvider.Manual, uid),
        type:
          payment.effect === PraktikaTransactionEffect.Cr
            ? TransactionType.Incoming
            : TransactionType.Outgoing,
        status: TransactionStatus.Complete,
        from: '',
        to: '',
        description: [payment.description, payment.notes]
          .filter((item) => item !== '')
          .join(' - '),
        amount: Math.abs(Money.fromCents(payment.amount)),
        extendedData: payment,
        createdAt: paymentRecord.record.translations.effectiveDate,
        practiceRef,
      }),
    };
  });
}

function getUsedAccountCredits(
  adjustments: IGetRecordResponse<PraktikaAppointmentInvoiceAdjustment>[],
  practiceRef: DocumentReference<IPractice>
): (IIdentifiable & ITransaction)[] {
  return adjustments
    .filter(
      (adjustment) =>
        adjustment.data.type_id === PraktikaTransationType.TransferFromDepositCr
    )
    .map((adjustmentRecord) => {
      const adjustment = adjustmentRecord.data;
      const uid = adjustment.id.toString();
      return Transaction.init({
        ...Transaction.internalReference(
          TransactionProvider.AccountCredit,
          uid
        ),
        type: TransactionType.Incoming,
        status: TransactionStatus.Complete,
        from: '',
        to: '',
        description: [
          adjustmentRecord.data.description,
          adjustmentRecord.data.notes,
        ]
          .filter((item) => item !== '')
          .join(' - '),
        amount: Math.abs(Money.fromCents(adjustment.amount)),
        extendedData: adjustment,
        createdAt: adjustmentRecord.record.translations.effectiveDate,
        practiceRef,
      });
    });
}

function getAdjustments(
  adjustments: IGetRecordResponse<PraktikaAppointmentInvoiceAdjustment>[],
  practiceRef: DocumentReference<IPractice>
): (IIdentifiable & ITransaction)[] {
  return adjustments
    .filter(
      (adjustmentRecord) =>
        ![
          PraktikaTransationType.TransferFromDepositCr,
          PraktikaTransationType.TransferFromDepositDr,
        ].includes(adjustmentRecord.data.type_id) &&
        !PraktikaSurcharge.isSurcharge(adjustmentRecord.data)
    )
    .map((adjustmentRecord) => {
      const adjustment = adjustmentRecord.data;
      const uid = adjustment.id.toString();
      return Transaction.init({
        ...Transaction.internalReference(TransactionProvider.Discount, uid),
        type: TransactionType.Incoming,
        status: TransactionStatus.Complete,
        from: '',
        to: '',
        description: [
          adjustmentRecord.data.description,
          adjustmentRecord.data.notes,
        ]
          .filter((item) => item !== '')
          .join(' - '),
        amount: Math.abs(Money.fromCents(adjustment.amount)),
        extendedData: adjustment,
        createdAt: adjustmentRecord.record.translations.effectiveDate,
        practiceRef,
      });
    });
}
